import React, { useState } from 'react';
import { Box, Typography, Button, Chip, Badge, Divider } from '@mui/material';
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { colors } from '../theme';
import { startToast, updateToastError, updateToastSuccess } from '../utils/notification-loading';

// Estendi dayjs con i plugin per timezone e UTC
dayjs.extend(utc);
dayjs.extend(timezone);

const IntelligenceTimeFilter = ({
  onDateChange, 
  initialDateFrom, 
  initialDateTo, 
  title = "Time Filter",
  compact = false,
  showTitle = true,
  disabledPresets = [],
  maxTimeRange = {}
}) => {
  const [, setError] = useState(false);
  const timezone = sessionStorage.getItem("timezone") || 'Europe/Amsterdam';

  const [dateFrom, setDateFrom] = useState(() =>
    initialDateFrom
      ? dayjs(initialDateFrom).tz(timezone)
      : dayjs().tz(timezone).subtract(7, 'day')
  );
  const [dateTo, setDateTo] = useState(() =>
    initialDateTo
      ? dayjs(initialDateTo).tz(timezone)
      : dayjs().tz(timezone)
  );

  // Il filtro è sempre attivo se abbiamo delle date impostate
  const isFilterActive = dateFrom && dateTo;

  // Preset date ranges - compatti per Intelligence
  const presets = [
    { label: '1H', hours: 1 },
    { label: '6H', hours: 6 },
    { label: '24H', hours: 24 },
    { label: '3D', days: 3 },
    { label: '7D', days: 7 },
    { label: '30D', days: 30 }
  ];

  const handlePresetClick = (preset) => {
    // Genera le date in UTC
    const to = dayjs();
    let from;
    
    if (preset.hours) {
      from = dayjs.utc().subtract(preset.hours, 'hour');
    } else {
      from = dayjs.utc().subtract(preset.days, 'day');
    }
    
    // Converte nella timezone per la visualizzazione
    const fromInTimezone = from.tz(timezone);
    const toInTimezone = to.tz(timezone);
    
    
    setDateFrom(fromInTimezone);
    setDateTo(toInTimezone);
    
    if (onDateChange) {
      const payload = {
        dateFrom: from,
        dateTo: to
      };
      onDateChange(payload);
    }
  };

  const checkDateRange = (fromDate, toDate) => {
    if (maxTimeRange.value && maxTimeRange.type) {
      startToast("Checking date range...");
      const from = dayjs.utc(fromDate).tz(timezone);
      const to = dayjs.utc(toDate).tz(timezone);
      let diff;
      if (maxTimeRange.type === 'hour') {
        diff = to.diff(from, 'hour');
      } else if (maxTimeRange.type === 'day') {
        diff = to.diff(from, 'day');
      }
      if (diff > maxTimeRange.value) {
        updateToastError("The date range is too long");
        return false;
      } else {
        updateToastSuccess("Date range is valid");
        return true;
      }
    }
    return true;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mb: 2 }}>
        {!compact && showTitle && (
          <Box display="flex" alignItems="center" mb={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Badge
                variant="dot"
                sx={{
                  '& .MuiBadge-dot': {
                    backgroundColor: isFilterActive ? '#ff9800' : 'transparent',
                    width: 8,
                    height: 8,
                  }
                }}
              >
                <CalendarTodayIcon sx={{ color: colors.grey[100], fontSize: 16 }} />
              </Badge>
              <Typography variant="subtitle1" fontWeight="bold" color={colors.grey[100]}>
                {title}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Quick filters e date picker inline */}
        <Box display="flex" alignItems="center" gap={compact ? 0.5 : 1} flexWrap="wrap">
          {/* Quick filters */}
          {presets.map((preset) => {
            if(disabledPresets.includes(preset.label)){
              return null;
            }else{
            
            return (
              <Chip
                key={preset.label}
                label={preset.label}
                size="small"
                onClick={() => handlePresetClick(preset)}
                sx={{
                  fontSize: compact ? '0.7rem' : '0.75rem',
                  height: compact ? 24 : 28,
                  color: colors.grey[100],
                  backgroundColor: colors.primary[400],
                  border: `1px solid ${colors.grey[600]}`,
                  opacity: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    color: colors.grey[900],
                    backgroundColor: "#459095",
                    borderColor: colors.grey[500]
                  },
                }}
              />
            );
          }
          })}
          
          <Divider orientation="vertical" flexItem sx={{ bgcolor: colors.grey[600], mx: 1, height: "auto" }} />
          
          {/* Date pickers compatti */}
          <DateTimeRangePicker
                value={[dateFrom, dateTo]}
                onChange={([newDateFrom, newDateTo]) => {
                  setError(false);
                  if (newDateFrom && newDateTo) {
                    // Assicurati che entrambe le date abbiano esattamente lo stesso timezone
                    const baseTimezone = timezone;
                    const fromDate = dayjs(newDateFrom).tz(baseTimezone);
                    const toDate = dayjs(newDateTo).tz(baseTimezone);
                    
                    // Verifica che abbiano lo stesso timezone
                    if (fromDate.format('Z') === toDate.format('Z')) {
                      setDateFrom(fromDate);
                      setDateTo(toDate);
                    } else {
                      // Se i timezone sono diversi, forza lo stesso timezone
                      setDateFrom(dayjs(newDateFrom).tz(baseTimezone));
                      setDateTo(dayjs(newDateTo).tz(baseTimezone));
                    }
                  }
                }}
                ampm={false}
                sx={{ width: 300 }}
              />

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if(checkDateRange(dateFrom, dateTo)){
                const payload = {
                dateFrom: dateFrom,
                dateTo: dateTo
              };
                onDateChange(payload);
              }else{
                setError(true);
              }
              
            }}
            sx={{
              height: 36,
              fontSize: '0.75rem',
              backgroundColor: colors.primary[400],
              '&:hover': {
                backgroundColor: colors.primary[800]
              }
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default IntelligenceTimeFilter;